import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Row, Col, Label, Input, Button, Badge,
} from 'reactstrap'
import {
  fetchLicenseSettings,
  saveLicenseSettings,
} from '../../actions/cspLicensesActions'
import cspApi from './cspApi'

// Typeahead picker for the Sales CW board. A plain HTML5 datalist
// gives us "dropdown + type to filter" with no JS dependency. The
// admin sees board names; the underlying state stores the integer
// board id. A name that doesn't match any loaded board clears the
// selection (id = 0), which the alerter treats as "log-only mode".
const BoardPicker = ({ boards, loading, selectedId, onSelect, disabled }) => {
  const selectedBoard = boards.find((b) => b.id === selectedId)
  // Track the visible text separately so the user can type freely
  // without re-rendering the input mid-keystroke. Resolve to id on
  // change.
  const [text, setText] = React.useState(selectedBoard ? selectedBoard.name : '')
  React.useEffect(() => {
    setText(selectedBoard ? selectedBoard.name : '')
  }, [selectedBoard])

  const onChange = (value) => {
    setText(value)
    const match = boards.find((b) => (b.name || '').toLowerCase() === value.toLowerCase())
    onSelect(match ? match.id : 0)
  }

  return (
    <>
      <Input type="text" bsSize="sm" list="csp-alerts-board-list"
        placeholder={loading ? 'Loading boards…' : 'Type or pick a board'}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
      />
      <datalist id="csp-alerts-board-list">
        {boards.map((b) => (
          <option key={b.id} value={b.name} />
        ))}
      </datalist>
    </>
  )
}

const ROLE_FIELDS = [
  { key: 'thresholdSeatsSales', label: 'Sales' },
  { key: 'thresholdSeatsITManager', label: 'IT Manager (Partner)' },
  { key: 'thresholdSeatsSSE', label: 'SSE' },
  { key: 'thresholdSeatsAdmin', label: 'Sales Admin' },
]

const parseInt0 = (v) => {
  if (v === '' || v == null) return 0
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
}

// Anti-mistake MPN ID editor. Locked read-only on open; explicit Edit
// button to enter edit mode; Save opens a hard-confirm modal showing
// the old → new diff. The MPN ID lands on every DD ManageSubscription
// call, so an accidental change here propagates to every customer's
// licence write until reverted - the friction is deliberate.
const MpnIdEditor = ({ persistedValue, draftValue, onDraftChange, isAdmin, onRequestSave }) => {
  const [editing, setEditing] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Cancelling reverts to the persisted value AND closes the edit field.
  const cancelEdit = () => {
    onDraftChange(persistedValue ?? '')
    setEditing(false)
  }

  const dirty = (draftValue ?? '') !== (persistedValue ?? '')

  const handleSaveClick = () => {
    if (!dirty) return
    setConfirmOpen(true)
  }
  const handleConfirm = () => {
    setConfirmOpen(false)
    setEditing(false)
    onRequestSave()
  }

  return (
    <>
      <Label className="font-weight-bold mb-1">Microsoft Partner ID (MPN ID)</Label>
      <Row className="align-items-center">
        <Col md={7}>
          <Input
            type="text"
            value={editing ? (draftValue ?? '') : (persistedValue || '— not set —')}
            onChange={(e) => onDraftChange(e.target.value)}
            disabled={!editing}
            bsSize="sm"
            maxLength={32}
            style={{
              fontFamily: 'monospace',
              backgroundColor: editing ? '#fff' : '#f6f8fa',
            }}
          />
        </Col>
        <Col xs="auto">
          {!editing ? (
            <Button
              color="outline-secondary"
              size="sm"
              disabled={!isAdmin}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button color="secondary" size="sm" className="mr-2" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button
                color="primary"
                size="sm"
                disabled={!dirty}
                onClick={handleSaveClick}
              >
                Save MPN ID…
              </Button>
            </>
          )}
        </Col>
      </Row>
      <p className="small text-muted mt-2 mb-0">
        OSIT's Microsoft Partner ID. Stamped on every licence change sent
        to Dicker Data so Microsoft pays the partner rebate to the right
        reseller. Some customers have their own partner arrangement —
        those are set per tenant and take precedence.
        {!isAdmin && ' (Read-only — only Admin can edit.)'}
      </p>

      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} size="md">
        <ModalHeader toggle={() => setConfirmOpen(false)}>
          Confirm change to MPN ID
        </ModalHeader>
        <ModalBody>
          <p>
            You're about to change the Microsoft Partner ID sent on every
            licence change to Dicker Data. Every change from now on will
            carry the new value until an admin updates it again.
          </p>
          <table className="table table-sm mb-3">
            <tbody>
              <tr>
                <td className="font-weight-bold" style={{ width: 100 }}>From</td>
                <td>
                  <code style={{ fontFamily: 'monospace' }}>
                    {persistedValue || '— empty —'}
                  </code>
                </td>
              </tr>
              <tr className="table-warning">
                <td className="font-weight-bold">To</td>
                <td>
                  <code style={{ fontFamily: 'monospace' }}>
                    {draftValue || '— empty —'}
                  </code>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="alert alert-warning small py-2 mb-0">
            A typo tags every later licence change to the wrong partner
            record. Check it against Partner Center.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirm}>
            Yes, change MPN ID
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

const CSPLicenseSettingsModal = ({ isOpen, toggle, isAdmin }) => {
  const dispatch = useDispatch()
  const settings = useSelector((s) => s.cspLicenses.settings)
  const settingsLoading = useSelector((s) => s.cspLicenses.settingsLoading)
  const settingsSaving = useSelector((s) => s.cspLicenses.settingsSaving)

  const [draft, setDraft] = useState(null)

  // CW pickers state lives in component scope (not redux) - one-shot
  // fetches when the modal opens; statuses re-fetch when the admin
  // changes the selected board.
  const [boards, setBoards] = useState([])
  const [priorities, setPriorities] = useState([])
  const [boardsLoading, setBoardsLoading] = useState(false)
  const [statuses, setStatuses] = useState([])

  useEffect(() => {
    if (isOpen) dispatch(fetchLicenseSettings())
  }, [isOpen, dispatch])

  useEffect(() => {
    if (isOpen && settings && !draft) setDraft({ ...settings })
  }, [isOpen, settings, draft])

  useEffect(() => {
    if (!isOpen) {
      setDraft(null)
      setBoards([])
      setStatuses([])
    }
  }, [isOpen])

  // Load Sales boards once when the modal opens. Admin-only endpoint;
  // for non-admins we leave boards empty and the picker renders an
  // empty datalist (the field is disabled anyway).
  useEffect(() => {
    if (!isOpen || !isAdmin) return
    let cancelled = false
    setBoardsLoading(true)
    cspApi.get('/csplicenses/cw-boards/sales')
      .then((res) => res.ok ? res.json() : [])
      .then((data) => { if (!cancelled) setBoards(Array.isArray(data) ? data : []) })
      .catch(() => { if (!cancelled) setBoards([]) })
      .finally(() => { if (!cancelled) setBoardsLoading(false) })
    return () => { cancelled = true }
  }, [isOpen, isAdmin])

  // Load CW priorities once when the modal opens. System-wide in CW, so unlike
  // statuses they don't depend on the selected board. Left at 0 the alerter
  // sends no priority and CW applies its own default, which here is "1H" - a
  // one-hour SLA on a ticket that only asks someone to review activity.
  useEffect(() => {
    if (!isOpen || !isAdmin) return
    let cancelled = false
    cspApi.get('/csplicenses/cw-priorities')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => { if (!cancelled) setPriorities(Array.isArray(data) ? data : []) })
      .catch(() => { if (!cancelled) setPriorities([]) })
    return () => { cancelled = true }
  }, [isOpen, isAdmin])

  // Re-fetch statuses whenever the selected board changes (initial
  // settings load + admin edits). Resets to empty when no board is
  // selected so the status picker shows the "select a board first"
  // placeholder.
  const selectedBoardId = draft?.anomalyCwBoardId ?? 0
  useEffect(() => {
    if (!isOpen || !isAdmin || selectedBoardId <= 0) {
      setStatuses([])
      return
    }
    let cancelled = false
    cspApi.get(`/csplicenses/cw-boards/${selectedBoardId}/statuses`)
      .then((res) => res.ok ? res.json() : [])
      .then((data) => { if (!cancelled) setStatuses(Array.isArray(data) ? data : []) })
      .catch(() => { if (!cancelled) setStatuses([]) })
    return () => { cancelled = true }
  }, [isOpen, isAdmin, selectedBoardId])

  // Save the non-MPNID parts of the modal (thresholds + test-API toggle).
  // Anomaly-alerter fields tag along on the threshold-save path so an
  // admin can tune them in one click. MPN ID still has its own
  // dedicated confirm-then-save flow because changing it is a
  // higher-stakes operation.
  const anomalyFromDraft = () => ({
    anomalySeatsPer24h: parseInt0(draft?.anomalySeatsPer24h),
    anomalySeatsPer48h: parseInt0(draft?.anomalySeatsPer48h),
    anomalyRequestsPer24h: parseInt0(draft?.anomalyRequestsPer24h),
    anomalyRequestsPer48h: parseInt0(draft?.anomalyRequestsPer48h),
    anomalyCwBoardId: parseInt0(draft?.anomalyCwBoardId),
    anomalyCwPriorityId: parseInt0(draft?.anomalyCwPriorityId),
    notifyCwPriorityId: parseInt0(draft?.notifyCwPriorityId),
    anomalyCwReopenStatus: draft?.anomalyCwReopenStatus ?? '',
  })
  const anomalyFromSettings = () => ({
    anomalySeatsPer24h: parseInt0(settings?.anomalySeatsPer24h),
    anomalySeatsPer48h: parseInt0(settings?.anomalySeatsPer48h),
    anomalyRequestsPer24h: parseInt0(settings?.anomalyRequestsPer24h),
    anomalyRequestsPer48h: parseInt0(settings?.anomalyRequestsPer48h),
    anomalyCwBoardId: parseInt0(settings?.anomalyCwBoardId),
    anomalyCwPriorityId: parseInt0(settings?.anomalyCwPriorityId),
    notifyCwPriorityId: parseInt0(settings?.notifyCwPriorityId),
    anomalyCwReopenStatus: settings?.anomalyCwReopenStatus ?? '',
  })

  const handleSaveThresholdsAndToggle = () => {
    if (!draft) return
    const sanitised = ROLE_FIELDS.reduce(
      (acc, f) => ({ ...acc, [f.key]: parseInt0(draft[f.key]) }),
      {
        useDickerDataTestApi: !!draft.useDickerDataTestApi,
        // Preserve the MPNID exactly as it was persisted - this Save
        // path does not edit it.
        mpnid: settings?.mpnid ?? '',
        ...anomalyFromDraft(),
      }
    )
    dispatch(saveLicenseSettings(sanitised))
  }

  // Save just the MPNID, after the confirm modal. Round-trips every
  // other field unchanged so threshold drafts in progress don't get
  // persisted prematurely.
  const handleSaveMpnId = () => {
    if (!draft) return
    const payload = ROLE_FIELDS.reduce(
      (acc, f) => ({ ...acc, [f.key]: parseInt0(settings?.[f.key] ?? 0) }),
      {
        useDickerDataTestApi: !!settings?.useDickerDataTestApi,
        mpnid: draft.mpnid ?? '',
        ...anomalyFromSettings(),
      }
    )
    dispatch(saveLicenseSettings(payload))
  }

  // Dirty applies only to the non-MPNID sections; MPNID has its own
  // dedicated save path with confirmation. Otherwise editing an MPN
  // draft would prematurely enable the bottom "Save" button.
  const ANOMALY_INT_FIELDS = [
    'anomalySeatsPer24h', 'anomalySeatsPer48h',
    'anomalyRequestsPer24h', 'anomalyRequestsPer48h',
    'anomalyCwBoardId',
    'anomalyCwPriorityId',
    'notifyCwPriorityId',
  ]
  const dirtyOther =
    draft &&
    (ROLE_FIELDS.some((f) => parseInt0(draft[f.key]) !== parseInt0(settings?.[f.key]))
      || (!!draft.useDickerDataTestApi !== !!settings?.useDickerDataTestApi)
      || ANOMALY_INT_FIELDS.some((k) => parseInt0(draft[k]) !== parseInt0(settings?.[k]))
      || (draft.anomalyCwReopenStatus ?? '') !== (settings?.anomalyCwReopenStatus ?? ''))

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        365 Licence Settings
        {dirtyOther && <Badge color="warning" className="ml-2">Unsaved</Badge>}
      </ModalHeader>
      <ModalBody>
        {settingsLoading && !draft ? (
          <div className="text-muted">Loading…</div>
        ) : !draft ? (
          <div className="text-muted">No settings loaded.</div>
        ) : (
          <>
            <p className="small text-muted">
              Seats each role can change without approval. Anything larger
              goes to an IT Manager, SSE or Admin.
            </p>
            {!isAdmin && (
              <div className="alert alert-secondary py-2 small">
                Read-only: only Admin can edit thresholds.
              </div>
            )}
            <Row>
              {ROLE_FIELDS.map((f) => (
                <Col md={6} key={f.key} className="mb-3">
                  <Label className="font-weight-bold">{f.label}</Label>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={draft[f.key] ?? 0}
                    onChange={(e) => setDraft({ ...draft, [f.key]: parseInt0(e.target.value) })}
                    disabled={!isAdmin}
                    bsSize="sm"
                  />
                </Col>
              ))}
            </Row>
            <p className="small text-muted mt-2 mb-3">
              Set to 0 to send every change through approval.
            </p>

            <hr />
            <MpnIdEditor
              persistedValue={settings?.mpnid}
              draftValue={draft.mpnid}
              onDraftChange={(v) => setDraft({ ...draft, mpnid: v })}
              isAdmin={isAdmin}
              onRequestSave={handleSaveMpnId}
            />

            <hr />
            <Label className="font-weight-bold mb-1">Dicker Data test API</Label>
            <div className="d-flex align-items-center mb-2">
              <Input
                type="checkbox"
                className="m-0 mr-2"
                style={{ position: 'relative', marginTop: 0, transform: 'scale(1.2)' }}
                checked={!!draft.useDickerDataTestApi}
                onChange={(e) => setDraft({ ...draft, useDickerDataTestApi: e.target.checked })}
                disabled={!isAdmin}
              />
              <span className="small">
                Send Dicker Data calls to their test environment.
              </span>
            </div>
            {settings && (
              <>
                <div className="d-flex align-items-center mb-2">
                  <span className="small mr-2">Currently routing to:</span>
                  <Badge color={settings.isDickerDataTestApiEffective ? 'warning' : 'danger'}>
                    {settings.isDickerDataTestApiEffective ? 'TEST' : 'LIVE'}
                  </Badge>
                  {!!draft.useDickerDataTestApi !== !!settings.useDickerDataTestApi && (
                    <span className="small text-muted ml-2">Save to apply your change.</span>
                  )}
                </div>

                {/*
                  The toggle is one of two keys, so "off" alone does not mean
                  live. Name the key that is actually holding routing rather
                  than leaving an admin to guess why nothing changed.
                */}
                {settings.isDickerDataTestApiEffective ? (
                  settings.isDickerDataTestApiForced ? (
                    <div className="alert alert-info py-2 small mb-0">
                      Held in test by <code>DickerData:UseTestApi</code>, not this
                      toggle. Ask Engineering to change it for this environment.
                    </div>
                  ) : settings.useDickerDataTestApi ? (
                    <div className="alert alert-warning py-2 small mb-0">
                      In test because this toggle is on. Turning it off will send
                      real requests to Dicker Data.
                    </div>
                  ) : (
                    <div className="alert alert-info py-2 small mb-0">
                      Both keys permit live calls, but something above them is
                      pinning test — in a Debug build,{' '}
                      <code>DickerData:AllowProdInDebug</code>.
                    </div>
                  )
                ) : (
                  <div className="alert alert-danger py-2 small mb-0">
                    Live. Changes here affect real subscriptions and real billing.
                  </div>
                )}

                <div className="small text-muted mt-2">
                  In test mode the daily sync and the anomaly alerter are both
                  paused. Use <em>Sync from Dicker Data</em> on the CSP Renewals
                  dashboard to pull data on demand.
                </div>
              </>
            )}

            <hr />
            <Label className="font-weight-bold mb-1">Alerts</Label>
            <p className="small text-muted">
              Opens a ticket on the Sales Admin queue when a tenant's
              licence activity crosses these limits.
            </p>
            <Row>
              <Col md={6} className="mb-2">
                <Label className="small">Seats / 24h</Label>
                <Input type="number" min={0} step={1} bsSize="sm"
                  value={draft.anomalySeatsPer24h ?? 0}
                  onChange={(e) => setDraft({ ...draft, anomalySeatsPer24h: parseInt0(e.target.value) })}
                  disabled={!isAdmin} />
              </Col>
              <Col md={6} className="mb-2">
                <Label className="small">Seats / 48h</Label>
                <Input type="number" min={0} step={1} bsSize="sm"
                  value={draft.anomalySeatsPer48h ?? 0}
                  onChange={(e) => setDraft({ ...draft, anomalySeatsPer48h: parseInt0(e.target.value) })}
                  disabled={!isAdmin} />
              </Col>
              <Col md={6} className="mb-2">
                <Label className="small">Requests / 24h</Label>
                <Input type="number" min={0} step={1} bsSize="sm"
                  value={draft.anomalyRequestsPer24h ?? 0}
                  onChange={(e) => setDraft({ ...draft, anomalyRequestsPer24h: parseInt0(e.target.value) })}
                  disabled={!isAdmin} />
              </Col>
              <Col md={6} className="mb-2">
                <Label className="small">Requests / 48h</Label>
                <Input type="number" min={0} step={1} bsSize="sm"
                  value={draft.anomalyRequestsPer48h ?? 0}
                  onChange={(e) => setDraft({ ...draft, anomalyRequestsPer48h: parseInt0(e.target.value) })}
                  disabled={!isAdmin} />
              </Col>
              <Col md={6} className="mb-2">
                <Label className="small">Sales Admin board</Label>
                <BoardPicker
                  boards={boards}
                  loading={boardsLoading}
                  selectedId={draft.anomalyCwBoardId ?? 0}
                  onSelect={(id) => setDraft({ ...draft, anomalyCwBoardId: id })}
                  disabled={!isAdmin}
                />
                {/*
                  0 means log-only: the alerter still detects and logs, but
                  raises no ticket. Nothing else says so, so an unset board
                  reads as "configured, quiet" rather than "no tickets will
                  ever arrive".
                */}
                {!(draft.anomalyCwBoardId > 0) && (
                  <div className="small text-danger mt-1">
                    No board set — anomalies are logged but no tickets are raised.
                  </div>
                )}
              </Col>
              <Col md={6} className="mb-2">
                <Label className="small">Anomaly ticket priority</Label>
                <Input type="select" bsSize="sm"
                  value={draft.anomalyCwPriorityId ?? 0}
                  onChange={(e) => setDraft({
                    ...draft,
                    anomalyCwPriorityId: parseInt(e.target.value, 10) || 0,
                  })}
                  disabled={!isAdmin}>
                  <option value={0}>&mdash; CW default &mdash;</option>
                  {priorities.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Input>
                <div className="small text-muted">
                  CW default is 1H. These tickets are for review, not an SLA.
                </div>
              </Col>
              <Col md={6} className="mb-2">
                <Label className="small">Change notification priority</Label>
                <Input type="select" bsSize="sm"
                  value={draft.notifyCwPriorityId ?? 0}
                  onChange={(e) => setDraft({
                    ...draft,
                    notifyCwPriorityId: parseInt(e.target.value, 10) || 0,
                  })}
                  disabled={!isAdmin}>
                  <option value={0}>&mdash; CW default &mdash;</option>
                  {priorities.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Input>
                <div className="small text-muted">
                  One ticket per applied licence change, on the board above. Informational
                  &mdash; set it lower than the anomaly priority.
                </div>
              </Col>
              <Col md={6} className="mb-2">
                <Label className="small">Re-fire status</Label>
                <Input type="select" bsSize="sm"
                  value={draft.anomalyCwReopenStatus ?? ''}
                  onChange={(e) => setDraft({ ...draft, anomalyCwReopenStatus: e.target.value })}
                  disabled={!isAdmin || (draft.anomalyCwBoardId ?? 0) <= 0}>
                  <option value="">{(draft.anomalyCwBoardId ?? 0) <= 0 ? '— select a board first —' : '— select —'}</option>
                  {statuses.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </Input>
              </Col>
            </Row>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={settingsSaving}>
          Close
        </Button>
        {isAdmin && (
          <Button
            color="primary"
            onClick={handleSaveThresholdsAndToggle}
            disabled={!dirtyOther || settingsSaving}
          >
            {settingsSaving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default CSPLicenseSettingsModal
